import { Link } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaClock,
  FaPhone,
  FaIdCard,
  FaFileAlt,
} from "react-icons/fa";
import {
  BuildingOffice2Icon,
  ClockIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <span className="text-blue-700 mt-0.5 text-lg shrink-0">{icon}</span>
    <p className="text-gray-700 text-sm">
      <span className="font-semibold">{label}:</span> {value}
    </p>
  </div>
);

const SIC = () => (
  <div className="p-6 md:p-8 space-y-6 max-w-4xl mx-auto">

    {/* Header */}
    <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 flex items-start gap-4">
      <span className="text-blue-700 text-3xl mt-0.5 shrink-0">
        <BuildingOffice2Icon className="h-8 w-8" />
      </span>
      <div>
        <h2 className="text-xl font-bold text-blue-950 mb-1">
          SIC — Serviço de Informação ao Cidadão (Presencial)
        </h2>
        <p className="text-blue-900 text-sm leading-relaxed">
          O SIC é o canal físico de atendimento garantido pela{" "}
          <strong>Lei nº 12.527/2011 (Lei de Acesso à Informação)</strong>. Qualquer pessoa
          pode solicitar informações públicas à Prefeitura de Serrinha de forma gratuita,
          sem necessidade de justificativa.
        </p>
      </div>
    </div>

    {/* Attendance info */}
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
      <h3 className="text-lg font-bold text-blue-950 flex items-center gap-2">
        <FaMapMarkerAlt className="text-blue-700" />
        Local e Horário de Atendimento
      </h3>
      <div className="space-y-3">
        <InfoRow
          icon={<FaMapMarkerAlt />}
          label="Endereço"
          value="Rua Manoel Joaquim de Souza, 150, Centro — Serrinha/RN, CEP 59.258-000"
        />
        <InfoRow
          icon={<FaClock />}
          label="Horário"
          value="Segunda a Sexta-feira, das 08h às 13h"
        />
        <InfoRow
          icon={<FaPhone />}
          label="Telefone"
          value="(84) 99999-9999"
        />
      </div>
    </div>

    {/* What to bring */}
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
      <h3 className="text-lg font-bold text-blue-950 flex items-center gap-2">
        <FaIdCard className="text-blue-700" />
        O Que Levar
      </h3>
      <p className="text-sm text-gray-600">
        Para solicitar informações no SIC presencial, traga:
      </p>
      <ul className="space-y-2">
        {[
          "Documento de identificação com foto (RG, CNH, Passaporte)",
          "CPF (opcional)",
          "Descrição clara da informação ou documento solicitado",
        ].map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
            <span className="w-2 h-2 rounded-full bg-blue-700 shrink-0 mt-1.5" />
            {item}
          </li>
        ))}
      </ul>
      <p className="text-xs text-gray-400 italic">
        Não é necessário apresentar justificativa para solicitar informações públicas.
      </p>
    </div>

    {/* Process timeline */}
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
      <h3 className="text-lg font-bold text-blue-950 flex items-center gap-2">
        <ClockIcon className="h-5 w-5 text-blue-700" />
        Como Funciona o Prazo
      </h3>
      <div className="space-y-3">
        {[
          { step: "1", label: "Protocolo", text: "Você recebe um número de protocolo no momento do atendimento." },
          { step: "2", label: "Análise", text: "A solicitação é encaminhada ao setor responsável para apuração." },
          { step: "3", label: "Resposta inicial", text: "Prazo de até 20 dias corridos para resposta, prorrogável por mais 10 dias." },
          { step: "4", label: "Recurso", text: "Caso a resposta seja negada ou insatisfatória, você pode entrar com recurso." },
        ].map(({ step, label, text }) => (
          <div key={step} className="flex items-start gap-4">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-900 text-white text-xs font-bold flex items-center justify-center">
              {step}
            </span>
            <div>
              <p className="text-sm font-semibold text-gray-800">{label}</p>
              <p className="text-sm text-gray-600">{text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* What can be requested */}
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
      <h3 className="text-lg font-bold text-blue-950 flex items-center gap-2">
        <FaFileAlt className="text-blue-700" />
        O Que Pode Ser Solicitado
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          "Documentos administrativos e contratos",
          "Dados sobre gastos e licitações",
          "Informações sobre servidores públicos",
          "Dados sobre obras e serviços",
          "Relatórios e planos de governo",
          "Atos normativos e legislação municipal",
        ].map((item) => (
          <div key={item} className="flex items-center gap-2 text-sm text-gray-700">
            <span className="w-2 h-2 rounded-full bg-blue-700 shrink-0" />
            {item}
          </div>
        ))}
      </div>
    </div>

    {/* Legal note + e-SIC CTA */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-start gap-3">
        <InformationCircleIcon className="h-5 w-5 text-yellow-700 shrink-0 mt-0.5" />
        <p className="text-sm text-yellow-800 leading-relaxed">
          A negativa de acesso à informação pública sem justificativa legal é vedada pela
          Lei nº 12.527/2011. O cidadão pode recorrer em caso de recusa.
        </p>
      </div>
      <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl flex flex-col justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-blue-950">Prefere o atendimento online?</p>
          <p className="text-xs text-blue-700 mt-0.5">
            Envie sua solicitação pelo e-SIC sem sair de casa.
          </p>
        </div>
        <Link
          to="/transparencia/esic"
          className="inline-flex items-center gap-2 self-start bg-blue-900 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition text-sm"
        >
          Acessar e-SIC <ArrowRightIcon className="h-4 w-4" />
        </Link>
      </div>
    </div>

  </div>
);

export default SIC;
